import { useEffect, useState } from 'react';
import { Loader2, Plus, ShieldAlert, UploadIcon } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { set, z } from 'zod';

import manufacturerProfileAvatar from '@/js/icons/manufacturer_profile_avatar.svg';
import verifiedIcon from '@/js/icons/verified_icon.svg';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, AvatarFallback } from '@radix-ui/react-avatar';
import { useNavigate } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import { setUserProfile } from '@/js/reduxstore/actions/authActions';
import { LockClosedIcon } from '@radix-ui/react-icons';

const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z
    .string()
    .email('Invalid email address')
    .optional()
    .or(z.literal('')),
  phoneNumber: z
    .string()
    .regex(/^\d+$/, 'Phone number must contain only numbers')
    .optional()
    .or(z.literal('')),
  companyName: z.string().optional(),
  companyRegistrationId: z.string().optional(),
  companyAddress: z.string().optional(),
  companyCity: z.string().optional(),
  companyState: z.string().optional(),
  companyCountry: z.string().optional(),
  companyContactNumber: z
    .string()
    .regex(/^\d+$/, 'Phone number must contain only numbers')
    .optional()
    .or(z.literal('')),
  companyEmailAddress: z
    .string()
    .email('Invalid email address')
    .optional()
    .or(z.literal('')),
  companyWebsite: z
    .string()
    .url('Invalid website URL')
    .optional()
    .or(z.literal('')),
  industryType: z.string().optional(),
  companyDescription: z.string().optional(),
  isFunded: z.string().optional(),
  fundingAmount: z.string().optional(),
});

const EditProfileScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const { name, email, company_name } =
    useSelector((state) => state?.authReducer?.userData) || {};
  const userProfile = useSelector((state) => state?.authReducer?.userProfile);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: name.split(' ')[0] || '',
      lastName: name.split(' ')[1] || '',
      emailAddress: email || '',
      phoneNumber: userProfile?.phone_number || '',
      companyName: company_name || '',
      companyRegistrationId: userProfile?.registration_number || '',
      companyAddress: userProfile?.address || '',
      companyCity: userProfile?.city || '',
      companyState: userProfile?.state || '',
      companyCountry: userProfile?.country || '',
      companyContactNumber: userProfile?.company_contact_number || '',
      companyEmailAddress: userProfile?.company_email || '',
      companyWebsite: userProfile?.company_website || '',
      industryType: userProfile?.industry_type || '',
      companyDescription: userProfile?.company_description || '',
      isFunded:
        userProfile?.is_funded === true
          ? 'yes'
          : userProfile?.is_funded === false
            ? 'no'
            : '' || '',
      fundingAmount: userProfile?.funding_amount || '',
    },
  });

  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const [manufacturers, setManufacturers] = useState([
    { name: '', email: '', projectType: '' },
  ]);

  // getting previous manufacturers
  useEffect(() => {
    const getManufacturers = async () => {
      try {
        const response = await apiClient.get('/api/previous-manufacturer/get');

        const responseData = response.data;
        console.log('Previous Manufacturer API response:', responseData);
        if (responseData?.data) {
          const formattedManufacturers = [
            ...responseData.data.map((item) => ({
              name: item.name || 'N/A',
              email: item.email || 'N/A',
              projectType: item.project_type || 'N/A',
            })),
            { name: '', email: '', projectType: '' }, // Empty entry at the end
          ];

          setManufacturers(formattedManufacturers);
        }
      } catch (error) {
        const error_message = error?.response?.data?.error_message;

        switch (error_message) {
          case 'Could not find buyer profile':
            break;
          default:
            console.error('Error: ', error_message || error);
            setError(
              error_message ||
                'A network error occurred. Please check your connection and try again.',
            );
            break;
        }
      }
    };
    getManufacturers();
  }, []);

  const handleAddManufacturer = () => {
    setManufacturers([
      ...manufacturers,
      { name: '', email: '', projectType: '' },
    ]);
  };

  const handleRemoveManufacturer = (index) => {
    setManufacturers(manufacturers.filter((_, i) => i !== index));
  };

  const handleManufacturerChange = (index, field, value) => {
    const updatedManufacturers = [...manufacturers];
    updatedManufacturers[index][field] = value;
    setManufacturers(updatedManufacturers);
  };

  const onSubmit = async (data) => {
    setError(null);

    const apiData = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.emailAddress,
      phone_number: data.phoneNumber,
      company_name: data.companyName,
      registration_number: data.companyRegistrationId,
      address: data.companyAddress,
      city: data.companyCity,
      state: data.companyState,
      country: data.companyCountry,
      company_contact_number: data.companyContactNumber,
      company_email: data.companyEmailAddress,
      company_website: data.companyWebsite,
      industry_type: data.industryType,
      company_description: data.companyDescription,
      is_funded:
        data.isFunded === 'yes' ? true : data.isFunded === 'no' ? false : null,
      funding_amount: data.fundingAmount,
    };

    console.log('Submitting to API: ', apiData);

    setProcessing(true);

    try {
      const response = await apiClient.put(
        '/api/buyer-profile/create',
        JSON.stringify(apiData),
      );

      const responseData = response.data;
      console.log('API response:', responseData);
      dispatch(setUserProfile(responseData?.data));
    } catch (error) {
      const error_message = error?.response?.data?.error_message;

      switch (error_message) {
        case 'Could not find buyer profile':
          break;
        default:
          console.error('Error: ', error_message || error);
          setError(
            error_message ||
              'A network error occurred. Please check your connection and try again.',
          );
          break;
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg px-8 py-4 font-roboto">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="">
          {/* photo */}
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row space-x-8 items-center">
              <img className="h-32 w-32" src={manufacturerProfileAvatar} />
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={handleAddManufacturer}
                  className="flex items-center space-x-2 text-lightPurple font-semibold mb-2 border-[0.5px] border-solid py-1 px-2 text-[14px] rounded border-lightPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200"
                >
                  <span>Upload New Photo</span>
                </button>
                <p className="text-[12px] text-gray-600">
                  JPG or PNG is allowed
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleAddManufacturer}
              className="flex items-center space-x-2 text-white bg-lightPurple font-semibold mb-2 border-2 border-solid py-1 px-2 text-base rounded border-lightPurple"
            >
              <span>Save</span>
            </button>
          </div>

          {/* Company Information */}
          <div className="mt-12">
            <h1 className="text-lightPurple font-semibold mb-7">
              Company Information
            </h1>
            <div className="grid grid-cols-2 gap-4 gap-x-10">
              {/* Company Name */}
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Company Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Company Name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Registration ID */}
              <FormField
                control={form.control}
                name="companyRegistrationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Company Registration/Tax ID Number
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Registration/Tax ID Number"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div></div>

            {/* Address */}
            <div className="mt-3">
              <FormField
                control={form.control}
                name="companyAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Address
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Company Address"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 gap-x-6 mt-3">
              <FormField
                control={form.control}
                name="companyCity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      City
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter City"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="companyState"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      State
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter State"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="companyCountry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Country
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Country"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 gap-x-10 mt-3">
              {/* Contact Number */}
              <FormField
                control={form.control}
                name="companyContactNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Company Contact Number
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Contact Number"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email Address */}
              <FormField
                control={form.control}
                name="companyEmailAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Company Email Address
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Email Address"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* companyWebsite */}
              <FormField
                control={form.control}
                name="companyWebsite"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Company Website
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Website URL"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Industry Type */}
              <FormField
                control={form.control}
                name="industryType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Industry Type
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Industry Type"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Company Description */}
              <div className="col-span-2">
                <FormField
                  control={form.control}
                  name="companyDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-500 font-semibold">
                        Company Description
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                          placeholder="Brief About Your Company"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="mt-20">
            <h1 className="text-lightPurple font-semibold mb-7">
              Personal Information
            </h1>
            <div className="grid grid-cols-2 gap-4 gap-x-10">
              {/* First Name */}
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      First Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter First Name"
                        {...field}
                        disabled
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Last Name */}
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Last Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Last Name"
                        {...field}
                        disabled
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email Address */}
              <FormField
                control={form.control}
                name="emailAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Your Email Address"
                        {...field}
                        disabled
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Phone Number */}
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                        placeholder="Enter Your Phone Number Here"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Funding Information */}
          <div className="mt-20">
            <h1 className="text-lightPurple font-semibold mb-7">
              Funding Information
            </h1>
            <div className="space-y-8 w-7/12">
              {/* Is Your Company Funded */}
              <FormField
                control={form.control}
                name="isFunded"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Is Your Company Funded?
                    </FormLabel>
                    <RadioGroup
                      className="flex space-x-8 items-center"
                      {...field}
                      onValueChange={field.onChange}
                    >
                      <div className="flex space-x-4 items-center">
                        <RadioGroupItem value="yes" id="funded-yes" />
                        <FormLabel htmlFor="funded-yes">Yes</FormLabel>
                      </div>
                      <div className="flex space-x-4 items-center">
                        <RadioGroupItem value="no" id="funded-no" />
                        <FormLabel htmlFor="funded-no">No</FormLabel>
                      </div>
                    </RadioGroup>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Funding Amount */}
              <FormField
                control={form.control}
                name="fundingAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-500 font-semibold">
                      Funding Amount
                    </FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        defaultValue={field.value}
                      >
                        <SelectTrigger className="w-8/12 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400">
                          <SelectValue placeholder="Enter Funding Amount" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-500k">0 - 500k</SelectItem>
                          <SelectItem value="500k-1M">500k - 1M</SelectItem>
                          <SelectItem value="1M+">1M+</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <p className="flex flex-row space-x-2 items-center text-sm text-slate-500 mt-2 font-semibold">
                <img className="h-6 w-6" src={verifiedIcon} />
                <span>
                  <span className="text-slate-500 font-bold">Makersharks</span>{' '}
                  will verify your company’s funding details on{' '}
                  <a
                    href="https://www.crunchbase.com"
                    target="_blank"
                    className="text-darkPurple"
                    rel="noreferrer"
                  >
                    Crunchbase
                  </a>
                  .
                </span>
              </p>
            </div>
          </div>

          {/* // TODO: GET THIS FLOW COMPLETED */}
          {/* Previous Manufacturer */}
          <div className="mt-20">
            <h1 className="text-lightPurple font-semibold">
              Previous Manufacturer
            </h1>
            <p className="text-slate-500 text-[13px] mb-4">
              Adding your previous manufacturing partners helps showcase your
              experience to potential collaborators.
            </p>
            <button
              type="button"
              onClick={handleAddManufacturer}
              className="flex items-center space-x-2 text-lightPurple font-semibold ml-auto mb-4 border-[0.5px] border-solid py-1 px-2 text-[14px] rounded border-lightPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200"
            >
              <Plus className="w-5 h-5" />
              <span>Add More</span>
            </button>

            <div className="space-y-4">
              {manufacturers.map((manufacturer, index) => (
                <div
                  key={index}
                  className="grid grid-cols-12 gap-4 items-center"
                >
                  {manufacturers.length > 1 ? (
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => handleRemoveManufacturer(index)}
                        className="text-red-500 font-bold"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <span className="col-span-1 text-center">{index + 1}.</span>
                  )}
                  <div className="col-span-4">
                    <Input
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                      placeholder="Manufacturer Name"
                      value={manufacturer.name}
                      onChange={(e) =>
                        handleManufacturerChange(index, 'name', e.target.value)
                      }
                    />
                  </div>
                  <div className="col-span-4">
                    <Input
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
                      placeholder="Email"
                      value={manufacturer.email}
                      onChange={(e) =>
                        handleManufacturerChange(index, 'email', e.target.value)
                      }
                    />
                  </div>
                  <div className="col-span-3">
                    <Select
                      onValueChange={(value) =>
                        handleManufacturerChange(index, 'projectType', value)
                      }
                      value={manufacturer.projectType}
                    >
                      <SelectTrigger
                        className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400 text-black`}
                      >
                        <SelectValue placeholder="Project Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="product">Product</SelectItem>
                        <SelectItem value="prototype">Prototype</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upload NDA */}
          <div className="mt-20">
            <h1 className="text-lightPurple font-semibold">Upload NDA</h1>
            <p className="text-slate-500 text-[13px] my-6">
              Uploading a signed Non-Disclosure Agreement (NDA) ensures
              confidentiality and protects sensitive project information shared
              between you and potential manufacturers.
            </p>
            <button
              type="button"
              onClick={handleAddManufacturer}
              className="flex items-center space-x-2 text-white bg-lightPurple font-semibold mb-4 p-2 text-[14px] rounded ease-in duration-200 hover:bg-darkerPurple"
            >
              <UploadIcon className="w-5 h-5" />
              <span>Upload Documents</span>
            </button>
          </div>

          <div className="mt-10 flex flex-row items-center justify-center space-x-2 ">
            {error ? (
              <>
                <ShieldAlert className="h-3 w-3" />
                <span className="text-xs text-red-600">{error}</span>
              </>
            ) : (
              <>
                <LockClosedIcon className="h-3 w-3" />
                <span className="text-xs text-[#2CD673]">
                  Your Info is safely secured
                </span>
              </>
            )}
          </div>

          {/* Submit Button */}
          <div className="w-full flex justify-center">
            <Button
              type="submit"
              disable={processing}
              className={`mt-3 px-4 py-2 h-12 w-36 hover:bg-darkerPurple ease-in duration-200 ${processing ? 'bg-gray-500' : 'bg-lightPurple'}`}
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="font-roboto font-semibold text-base">
                    Saving...
                  </span>
                </>
              ) : (
                <span className="font-roboto font-semibold text-base">
                  Save
                </span>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default EditProfileScreen;
